import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getGatewaySettings,
  requestGatewayChangeCode,
  confirmGatewayChange,
} from "../../services/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";

const RESEND_SECONDS = 60;

const errorText = (err) =>
  err?.response?.data?.message ||
  err?.response?.data?.detail ||
  err?.message ||
  "Something went wrong. Please try again.";

const shorten = (value) =>
  value && value.length > 44 ? `${value.slice(0, 26)}…${value.slice(-8)}` : value;

const GatewaySettings = ({ gateway }) => {
  const queryClient = useQueryClient();
  const queryKey = ["gatewaySettings", gateway];
  const { data: current, isLoading, error } = useQuery({
    queryKey,
    queryFn: () => getGatewaySettings(gateway),
  });

  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("details");
  const [values, setValues] = useState({});
  const [shown, setShown] = useState({});
  const [requestId, setRequestId] = useState(null);
  const [sentTo, setSentTo] = useState("");
  const [code, setCode] = useState("");
  const [message, setMessage] = useState("");
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const openDialog = () => {
    const initial = {};
    current.fields.forEach((f) => {
      initial[f.name] = f.secret ? "" : f.value || "";
    });
    setValues(initial);
    setShown({});
    setStep("details");
    setRequestId(null);
    setCode("");
    setMessage("");
    setCooldown(0);
    setOpen(true);
  };

  const requestCode = useMutation({
    mutationFn: () => {
      const payload = {};
      current.fields.forEach((f) => {
        payload[f.name] = (values[f.name] || "").trim();
      });
      return requestGatewayChangeCode(gateway, payload);
    },
    onSuccess: (data) => {
      setRequestId(data.request_id);
      setSentTo(data.sent_to);
      setCode("");
      setMessage("");
      setCooldown(RESEND_SECONDS);
      setStep("code");
    },
    onError: (err) => setMessage(errorText(err)),
  });

  const confirm = useMutation({
    mutationFn: () => confirmGatewayChange(gateway, { request_id: requestId, code: code.trim() }),
    onSuccess: (data) => {
      setMessage(data.message);
      setStep("done");
      queryClient.invalidateQueries({ queryKey });
    },
    onError: (err) => setMessage(errorText(err)),
  });

  const hasChanges =
    current &&
    current.fields.some((f) =>
      f.secret ? (values[f.name] || "").trim() !== "" : (values[f.name] || "").trim() !== (f.value || "")
    );

  return (
    <div className="mt-8 bg-white rounded-xl shadow-sm">
      <div className="px-8 py-6 border-b border-gray-200 flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">
            Payment Gateway ({current?.label || gateway})
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {current?.notes} Changes need a verification code sent to{" "}
            {current?.verification_email || "the approver"}.
          </p>
        </div>
        <Button
          onClick={openDialog}
          disabled={isLoading || !!error}
          className="bg-[#0d3d2e] hover:bg-[#0f4a38] text-white text-sm px-5 shrink-0"
        >
          Change details
        </Button>
      </div>

      <div className="p-8">
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading current details…</p>
        ) : error ? (
          <p className="text-sm text-red-500">{errorText(error)}</p>
        ) : (
          <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-5 text-sm">
            {current.fields.map((f) => (
              <div key={f.name}>
                <dt className="text-xs font-medium text-gray-500 mb-1">{f.label}</dt>
                <dd
                  className="font-mono text-gray-900 break-all"
                  title={f.secret ? undefined : f.value}
                >
                  {f.secret ? f.hint : shorten(f.value)}
                </dd>
              </div>
            ))}
            <div>
              <dt className="text-xs font-medium text-gray-500 mb-1">Last changed</dt>
              <dd className="text-gray-900">
                {current.source === "database" && current.updated_at
                  ? `${new Date(current.updated_at).toLocaleString()}${
                      current.updated_by ? ` by ${current.updated_by}` : ""
                    }`
                  : "Never changed from this page (using the original setup)"}
              </dd>
            </div>
          </dl>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {step === "details" && current && (
            <>
              <DialogHeader>
                <DialogTitle>Change {current.label} details</DialogTitle>
                <DialogDescription>
                  We first check these details with {current.label}, then email a 6-digit code to{" "}
                  {current.verification_email}. Nothing is saved until you enter that code. Leave a
                  field as it is to keep it unchanged.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                {current.fields.map((f) => (
                  <div key={f.name}>
                    <Label className="text-sm font-medium text-gray-700 mb-1 block">{f.label}</Label>
                    <div className="flex gap-2">
                      <Input
                        type={f.secret && !shown[f.name] ? "password" : "text"}
                        value={values[f.name] || ""}
                        onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                        placeholder={f.secret ? `Leave blank to keep ${f.hint}` : ""}
                        autoComplete={f.secret ? "new-password" : "off"}
                        className="font-mono text-sm flex-1"
                      />
                      {f.secret && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShown((s) => ({ ...s, [f.name]: !s[f.name] }))}
                          className="text-xs"
                        >
                          {shown[f.name] ? "Hide" : "Show"}
                        </Button>
                      )}
                    </div>
                    {f.help && <p className="text-xs text-gray-500 mt-1">{f.help}</p>}
                  </div>
                ))}
                {message && <p className="text-sm text-red-600">{message}</p>}
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setMessage("");
                    requestCode.mutate();
                  }}
                  disabled={!hasChanges || requestCode.isPending}
                  className="bg-[#0d3d2e] hover:bg-[#0f4a38] text-white"
                >
                  {requestCode.isPending ? `Checking with ${current.label}…` : "Send verification code"}
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "code" && (
            <>
              <DialogHeader>
                <DialogTitle>Enter verification code</DialogTitle>
                <DialogDescription>
                  A 6-digit code was sent to <strong>{sentTo}</strong>. It expires in 10 minutes.
                  Enter it below to save the new {current?.label} details.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  className="text-center text-2xl tracking-[0.5em] font-mono"
                />
                {message && <p className="text-sm text-red-600">{message}</p>}
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Wrong details?</span>
                  <button
                    type="button"
                    className="underline hover:text-gray-800"
                    onClick={() => {
                      setMessage("");
                      setStep("details");
                    }}
                  >
                    Go back and edit
                  </button>
                </div>
                <div className="text-xs text-gray-500">
                  {cooldown > 0 ? (
                    <span>You can ask for a new code in {cooldown}s.</span>
                  ) : (
                    <button
                      type="button"
                      className="underline hover:text-gray-800"
                      onClick={() => {
                        setMessage("");
                        requestCode.mutate();
                      }}
                      disabled={requestCode.isPending}
                    >
                      {requestCode.isPending ? "Sending…" : "Send a new code"}
                    </button>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    setMessage("");
                    confirm.mutate();
                  }}
                  disabled={code.length !== 6 || confirm.isPending}
                  className="bg-[#0d3d2e] hover:bg-[#0f4a38] text-white"
                >
                  {confirm.isPending ? "Verifying…" : "Verify & save"}
                </Button>
              </DialogFooter>
            </>
          )}

          {step === "done" && (
            <>
              <DialogHeader>
                <DialogTitle>{current?.label} details updated</DialogTitle>
                <DialogDescription>{message}</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  onClick={() => setOpen(false)}
                  className="bg-[#0d3d2e] hover:bg-[#0f4a38] text-white"
                >
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default GatewaySettings;
